import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { getCurrentUser } from '../auth/useUser';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { newIdempotencyKey } from '../lib/idempotency';
import { ApiProblem } from '../api/client';
import {
  listCustomers,
  createCustomer,
  updateCustomer,
  setCustomerStatus,
  setCustomerReseller,
  setCustomerParent,
  CUSTOMER_ID_RE,
  type Customer,
  type CredentialCleanup,
  type RequesterContact,
} from '../api/customers';
import {
  listIntakeClients,
  rotateIntakeClientSecret,
  setIntakeClientEnabled,
  type IntakeClient,
} from '../api/intakeClients';
import { listCustomerUsers, type CustomerUser } from '../api/customerUsers';
import { listIntakeQueue, type IntakeSubmission } from '../api/intake';
import { getCase } from '../api/cases';
import { CustomerLogins } from '../components/CustomerLogins';

// #323 — Customers. Replaces the old Customer API page, which listed
// credentials with no record of the company that owned them: `customer_id` was
// free text whose only authority was a claim mapper on a Keycloak client, so a
// portal-only customer was impossible and there was nowhere to record anything
// about the company itself.
//
// This is master-detail on purpose. The company is now the thing that exists;
// its intake clients, portal logins and registrations are all views of it, so
// they are tabs inside a company rather than four global lists an operator has
// to cross-reference by eye.
//
// The load-bearing interaction is Suspend. It is the destructive action here and
// it does two things that can disagree: it writes SUSPENDED to the database
// (which is what actually refuses the company's requests, immediately) and then
// best-effort disables its Keycloak credentials. The second half can partly
// fail, so the response carries a per-credential `credentialCleanup` report and
// this page renders it item by item — a generic "suspended" toast would hide a
// still-live machine credential, which is the one outcome an operator must not
// miss.

const INTAKE_DOCS_URL = '/api/v1/intake/docs/';

const TABS = [
  ['overview', 'Overview'],
  ['clients', 'API Clients'],
  ['logins', 'Portal Logins'],
  ['registrations', 'Registrations'],
  ['subaccounts', 'Subaccounts'],
] as const;

type DetailTab = (typeof TABS)[number][0];

// #867 — Subaccounts is a reseller's tab only. Every other company has none, so
// offering it there would only ever show an empty list.
function tabsFor(c: Customer): ReadonlyArray<(typeof TABS)[number]> {
  return c.isReseller ? TABS : TABS.filter(([id]) => id !== 'subaccounts');
}

const resellerBadge: React.CSSProperties = { ...styles.badge, background: C.infoBg, color: C.info };

function statusBadge(status: string): React.CSSProperties {
  return status === 'ACTIVE'
    ? { ...styles.badge, background: C.successBg, color: C.success }
    : { ...styles.badge, background: C.dangerBg, color: C.danger };
}

// One row of the cleanup report. 'failed' is the only outcome that needs an
// operator to do something, so it is the only one colored as a problem.
function outcomeColor(outcome: string): string {
  if (outcome === 'failed') return C.danger;
  if (outcome === 'already-disabled') return C.textMuted;
  return C.success;
}

const OUTCOME_LABEL: Record<string, string> = {
  disabled: 'Disabled',
  'already-disabled': 'Already disabled',
  failed: 'STILL ENABLED — failed',
};

const KIND_LABEL: Record<string, string> = {
  'intake-client': 'API client',
  'portal-login': 'Portal login',
};

/**
 * What the last status change actually did to the company's credentials.
 *
 * Rendered from the API's response rather than assumed, because the DB write and
 * the Keycloak cleanup are separate steps and only the first is guaranteed. A
 * failed item means a credential that can still mint tokens for a company we
 * have just suspended, so it is called out above the list, not buried in it.
 */
function CleanupReport({ report, onDismiss }: { report: CredentialCleanup; onDismiss: () => void }) {
  const failed = report.items.filter((i) => i.outcome === 'failed');
  const border = failed.length > 0 ? C.danger : C.success;
  return (
    <div
      style={{ ...styles.card, borderLeft: `3px solid ${border}`, marginBottom: 16 }}
      role="alert"
      data-testid="cleanup-report"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ fontWeight: 600, color: failed.length > 0 ? C.danger : C.text }}>
          {failed.length > 0
            ? `${failed.length} credential${failed.length === 1 ? '' : 's'} could not be disabled — disable ${failed.length === 1 ? 'it' : 'them'} by hand`
            : 'Status change applied'}
        </div>
        <button
          type="button"
          aria-label="Dismiss credential report"
          style={{ ...styles.btnUnstyled, color: C.textMuted, fontWeight: 700 }}
          onClick={onDismiss}
        >×</button>
      </div>

      <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: report.items.length > 0 ? 12 : 0 }}>
        {report.note}
      </div>

      {report.items.length > 0 && (
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Credential</th>
            <th style={styles.th}>Identifier</th>
            <th style={styles.th}>Outcome</th>
          </tr></thead>
          <tbody>
            {report.items.map((item) => (
              <tr key={`${item.kind}:${item.id}`}>
                <td style={styles.td}>{KIND_LABEL[item.kind] ?? item.kind}</td>
                <td style={styles.td}><code style={{ fontSize: 13 }}>{item.id}</code></td>
                <td style={styles.td}>
                  <span style={{ color: outcomeColor(item.outcome), fontWeight: item.outcome === 'failed' ? 600 : 400 }}>
                    {OUTCOME_LABEL[item.outcome] ?? item.outcome}
                  </span>
                  {item.detail && (
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{item.detail}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* An attempted cleanup that touched nothing is worth saying out loud:
          it means the company has no credentials, not that something broke. */}
      {report.attempted && report.items.length === 0 && (
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>
          This company had no Keycloak credentials to disable.
        </div>
      )}
    </div>
  );
}

export function Customers() {
  const user = getCurrentUser();
  const { showToast } = useAppData();
  const { modal, setModal, setModalData, modalData, openCase, openSubmission } = useUIState();

  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Credentials and registrations are loaded once, globally, and grouped by
  // company: the list needs per-company counts, and one call each beats one
  // call per row.
  const [clients, setClients] = useState<IntakeClient[]>([]);
  const [logins, setLogins] = useState<CustomerUser[]>([]);
  const [submissions, setSubmissions] = useState<IntakeSubmission[]>([]);
  // The Keycloak-backed halves 503 until bcid-admin-svc is provisioned. The
  // customers table does not, so the company list still works — only the
  // credential counts and tabs are unavailable.
  const [notConfigured, setNotConfigured] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('overview');

  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ customerId: '', name: '' });

  const [savingName, setSavingName] = useState(false);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [contactsDraft, setContactsDraft] = useState<RequesterContact[] | null>(null);
  const [savingContacts, setSavingContacts] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [cleanup, setCleanup] = useState<CredentialCleanup | null>(null);
  // #867 — the reseller flag and the parent link.
  const [hierarchyBusy, setHierarchyBusy] = useState(false);
  const [parentDraft, setParentDraft] = useState('');

  const [busyClientId, setBusyClientId] = useState<string | null>(null);
  const [confirmClientId, setConfirmClientId] = useState<string | null>(null);
  // #539 — the case ID whose Case is being fetched for navigation, or null.
  const [openingCaseId, setOpeningCaseId] = useState<string | null>(null);
  // #691 — the case ID whose Case is being fetched to open the delete dialog.
  const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);

  const reportError = useCallback((err: unknown) => {
    if (err instanceof ApiProblem) showToast(err.detail ? `${err.title}: ${err.detail}` : err.title);
    else showToast(err instanceof Error ? err.message : String(err));
  }, [showToast]);

  const loadCustomers = useCallback(() => {
    setLoadError(null);
    return listCustomers()
      .then(setCustomers)
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : String(err)));
  }, []);

  const loadCredentials = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([listIntakeClients(), listCustomerUsers()]);
      setClients(c);
      setLogins(u);
      setNotConfigured(false);
    } catch (err) {
      if (err instanceof ApiProblem && err.status === 503) { setNotConfigured(true); return; }
      reportError(err);
    }
  }, [reportError]);

  const loadRegistrations = useCallback(async () => {
    try {
      // 200 is the route's maximum. The count is a "how much is going on here"
      // signal, not an accounting figure, so a very large backlog can under-read
      // rather than this page paging through everything on mount.
      const page = await listIntakeQueue({ limit: 200 });
      setSubmissions(page.items);
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  // #539 — open the case a submission was promoted into.
  //
  // The Registrations row carries only the case ID; `openCase` needs the whole
  // `Case`, so this fetches it the same way Intake does after a promote
  // (pages/Intake.tsx). It is a fetch and not a lookup on purpose: this page
  // never loads the case list, and adding one to resolve a click nobody may make
  // would be a request per visit instead of a request per click.
  //
  // A failure toasts and stays put. Navigating to a half-built detail view would
  // be worse than not navigating, and the case ID is still on screen to copy.
  //
  // THE RESULT IS GUARDED, not just the button. `getCase` is a round trip, and
  // during it the operator can open a submission from the very same row — which
  // navigates to Intake immediately. When the case then arrives, `openCase`
  // writes to provider state that this page's unmount does not touch, so the
  // operator is thrown off the submission panel they had just opened and onto
  // CaseDetail. Nothing errors; they simply end up somewhere they did not ask
  // for.
  //
  // Disabling the submission button for the duration would close this one route
  // and leave the underlying fault — a stale response that still navigates —
  // intact for the next one, at the cost of a dead link during an unrelated
  // fetch. So the in-flight id is held in a ref, cleared the moment the operator
  // chooses a different destination, and a response whose id is no longer
  // current is dropped: no navigation, and no toast either, since the failure of
  // a request the operator has already walked away from is not news.
  const openingCaseIdRef = useRef<string | null>(null);

  const cancelCaseOpen = useCallback(() => {
    openingCaseIdRef.current = null;
    setOpeningCaseId(null);
  }, []);

  const openPromotedCase = useCallback(async (caseId: string) => {
    openingCaseIdRef.current = caseId;
    setOpeningCaseId(caseId);
    try {
      const theCase = await getCase(caseId);
      if (openingCaseIdRef.current !== caseId) return;
      openCase(theCase, 'customers');
    } catch (err) {
      if (openingCaseIdRef.current !== caseId) return;
      showToast(`Could not open case ${caseId}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (openingCaseIdRef.current === caseId) cancelCaseOpen();
    }
  }, [openCase, showToast, cancelCaseOpen]);

  // #691 — open the delete-registration dialog for the case a submission was
  // promoted into. The dialog needs the whole `Case` (its version for If-Match,
  // its registrant for the typed confirmation, its sync status for the CTIA
  // warning) and this page holds none of it, so the click fetches it first —
  // the same trade `openPromotedCase` makes above, for the same reason.
  //
  // Only one such fetch can be in flight: every Delete button on the tab is
  // disabled while `deletingCaseId` is set, so the `finally` cannot clear a
  // newer click's state.
  const confirmDeleteRegistration = useCallback(async (caseId: string) => {
    setDeletingCaseId(caseId);
    try {
      const theCase = await getCase(caseId);
      setModalData({
        case: theCase,
        // #715 — refetch rather than remember. The queue now reports
        // `promotedCaseDeletedAt` per row, so the deleted state survives a
        // reload and comes from the server that did the delete; #691's
        // visit-local set of ids said the same thing for as long as the page
        // stayed open and then forgot it.
        onDeleted: () => { void loadRegistrations(); },
      });
      setModal('delete-registration');
    } catch (err) {
      reportError(err);
    } finally {
      setDeletingCaseId(null);
    }
  }, [loadRegistrations, reportError, setModal, setModalData]);

  // Opening a submission is choosing a different destination, so it supersedes
  // any case open still in flight.
  const openLinkedSubmission = useCallback((submissionId: string) => {
    cancelCaseOpen();
    openSubmission(submissionId);
  }, [cancelCaseOpen, openSubmission]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadCredentials(); }, [loadCredentials]);
  useEffect(() => { loadRegistrations(); }, [loadRegistrations]);

  // The create-intake-client modal reports back through modalData rather than a
  // callback, so reload the credential lists when it closes after a change.
  useEffect(() => {
    if (modal === null && modalData?.intakeClientsChanged) loadCredentials();
  }, [modal, modalData?.intakeClientsChanged, loadCredentials]);

  const clientsByCustomer = useMemo(() => {
    const map = new Map<string, IntakeClient[]>();
    for (const c of clients) {
      if (!c.customerId) continue;
      const list = map.get(c.customerId) ?? [];
      list.push(c);
      map.set(c.customerId, list);
    }
    return map;
  }, [clients]);

  const loginsByCustomer = useMemo(() => {
    const map = new Map<string, CustomerUser[]>();
    for (const u of logins) {
      if (!u.customerId) continue;
      const list = map.get(u.customerId) ?? [];
      list.push(u);
      map.set(u.customerId, list);
    }
    return map;
  }, [logins]);

  const submissionsByCustomer = useMemo(() => {
    const map = new Map<string, IntakeSubmission[]>();
    for (const s of submissions) {
      const list = map.get(s.customerId) ?? [];
      list.push(s);
      map.set(s.customerId, list);
    }
    return map;
  }, [submissions]);

  const customerList = customers ?? [];

  // #867 — derived from the one list this page already loads, rather than a
  // second endpoint: every customer row carries its parent.
  const byId = useMemo(() => new Map(customerList.map((c) => [c.customerId, c])), [customerList]);
  const childrenOf = useMemo(() => {
    const map = new Map<string, Customer[]>();
    for (const c of customerList) {
      if (!c.parentCustomerId) continue;
      const list = map.get(c.parentCustomerId) ?? [];
      list.push(c);
      map.set(c.parentCustomerId, list);
    }
    return map;
  }, [customerList]);
  const { filtered, bar } = useSearchFilter(
    customerList,
    (c) => [c.name, c.customerId].join(' '),
    [
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: '', label: 'All statuses' },
          { value: 'ACTIVE', label: 'Active only' },
          { value: 'SUSPENDED', label: 'Suspended only' },
        ],
        predicate: (c, v) => c.status === v,
      },
    ],
    { placeholder: 'Search companies by name or customer ID…' },
  );

  const selected = customerList.find((c) => c.customerId === selectedId) ?? null;

  const select = useCallback((customerId: string) => {
    setSelectedId(customerId);
    setTab('overview');
    setNameDraft(null);
    setContactsDraft(null);
    setConfirmStatus(false);
    setCleanup(null);
    setParentDraft('');
  }, []);

  const submitCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const customerId = form.customerId.trim();
    const name = form.name.trim();
    if (!CUSTOMER_ID_RE.test(customerId)) {
      showToast('Customer ID must be lowercase letters, numbers, and hyphens; 2–31 characters; starting with a letter or number.');
      return;
    }
    setCreating(true);
    try {
      await createCustomer({ customerId, name });
      setForm({ customerId: '', name: '' });
      setShowCreate(false);
      await loadCustomers();
      select(customerId);
      showToast(`${name} created. It can now have portal logins, an API client, or both.`, 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setCreating(false);
    }
  }, [form, loadCustomers, reportError, select, showToast]);

  const saveName = useCallback(async () => {
    if (!selected || nameDraft === null) return;
    const name = nameDraft.trim();
    if (name.length === 0) { showToast('Name must not be blank.'); return; }
    setSavingName(true);
    try {
      await updateCustomer(selected.customerId, { name });
      await loadCustomers();
      setNameDraft(null);
      showToast('Company name updated.', 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setSavingName(false);
    }
  }, [loadCustomers, nameDraft, reportError, selected, showToast]);

  // Requester contacts are free-form reference data, not a permission list, so
  // they are edited as a whole array and PATCHed as a whole array — the API
  // replaces the column outright. Blank rows are dropped rather than rejected:
  // a half-filled row is a row the admin abandoned, not an error to argue about.
  const saveContacts = useCallback(async () => {
    if (!selected || contactsDraft === null) return;
    const cleaned = contactsDraft
      .map((c) => ({
        name: c.name?.trim() || undefined,
        email: c.email?.trim() || undefined,
        phone: c.phone?.trim() || undefined,
      }))
      .filter((c) => c.name || c.email || c.phone);
    setSavingContacts(true);
    try {
      await updateCustomer(selected.customerId, { requesterContacts: cleaned });
      await loadCustomers();
      setContactsDraft(null);
      showToast('Requester contacts updated.', 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setSavingContacts(false);
    }
  }, [contactsDraft, loadCustomers, reportError, selected, showToast]);

  const changeStatus = useCallback(async (next: 'ACTIVE' | 'SUSPENDED') => {
    if (!selected) return;
    setStatusBusy(true);
    setConfirmStatus(false);
    try {
      const result = await setCustomerStatus(selected.customerId, next);
      // The report is the point of this call, so it is rendered rather than
      // toasted: a toast disappears before an operator can act on a failure.
      setCleanup(result.credentialCleanup);
      await loadCustomers();
    } catch (err) {
      reportError(err);
    } finally {
      setStatusBusy(false);
    }
  }, [loadCustomers, reportError, selected]);

  // #867 — both writes are refused by the database when they would break the
  // one-level rule (409), and the problem detail names the rule, so the toast
  // is the API's sentence rather than a guess at why.
  const changeReseller = useCallback(async (next: boolean) => {
    if (!selected) return;
    setHierarchyBusy(true);
    try {
      await setCustomerReseller(selected.customerId, next);
      await loadCustomers();
      showToast(next ? `${selected.name} is now a reseller.` : `${selected.name} is no longer a reseller.`, 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setHierarchyBusy(false);
    }
  }, [loadCustomers, reportError, selected, showToast]);

  const changeParent = useCallback(async (parentCustomerId: string | null) => {
    if (!selected) return;
    setHierarchyBusy(true);
    try {
      await setCustomerParent(selected.customerId, parentCustomerId);
      await loadCustomers();
      setParentDraft('');
      showToast(parentCustomerId
        ? `${selected.name} is now a subaccount of ${byId.get(parentCustomerId)?.name ?? parentCustomerId}.`
        : `${selected.name} is no longer a subaccount.`, 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setHierarchyBusy(false);
    }
  }, [byId, loadCustomers, reportError, selected, showToast]);

  const rotate = useCallback(async (c: IntakeClient) => {
    setBusyClientId(c.clientId);
    try {
      const result = await rotateIntakeClientSecret(c.clientId, newIdempotencyKey());
      setModalData((d: Record<string, unknown>) => ({
        ...d,
        rotatedSecret: { clientId: result.clientId, secret: result.clientSecret },
        intakeClientsChanged: Date.now(),
      }));
      setModal('create-intake-client');
    } catch (err) {
      reportError(err);
    } finally {
      setBusyClientId(null);
    }
  }, [reportError, setModal, setModalData]);

  const toggleClient = useCallback(async (c: IntakeClient) => {
    setBusyClientId(c.clientId);
    setConfirmClientId(null);
    try {
      await setIntakeClientEnabled(c.clientId, !c.enabled);
      await loadCredentials();
      showToast(c.enabled
        ? 'Client disabled. Its tokens no longer authenticate; historical submissions are retained.'
        : 'Client enabled.', 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusyClientId(null);
    }
  }, [loadCredentials, reportError, showToast]);

  // The company travels with the modal instead of being re-typed there: the
  // tenant is baked into the client's protocol mapper permanently, and this
  // button only exists on a company's own Credentials tab.
  const openCreateClient = () => {
    if (!selected) return;
    setModalData((d: Record<string, unknown>) => ({
      ...d,
      rotatedSecret: undefined,
      customerId: selected.customerId,
      customerName: selected.name,
    }));
    setModal('create-intake-client');
  };

  // Defense-in-depth: the API 403s non-admins regardless.
  if (!user?.canAdmin) {
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>Customers</h2>
        <div style={{ ...styles.card, color: C.danger }}>Administrator access required.</div>
      </div>
    );
  }

  // ── Detail ───────────────────────────────────────────────────────────────
  if (selected) {
    const myClients = clientsByCustomer.get(selected.customerId) ?? [];
    const mySubmissions = submissionsByCustomer.get(selected.customerId) ?? [];
    const suspended = selected.status === 'SUSPENDED';
    const detailTabs = tabsFor(selected);
    // A tab that has just disappeared (un-flagging a reseller drops
    // Subaccounts) falls back to the Overview rather than to a blank page.
    const activeTab: DetailTab = detailTabs.some(([id]) => id === tab) ? tab : 'overview';
    const parent = selected.parentCustomerId ? byId.get(selected.parentCustomerId) ?? null : null;
    const myChildren = childrenOf.get(selected.customerId) ?? [];
    // Who this company could be attached to: a reseller that is not itself a
    // subaccount, and not this company. The API refuses anything else anyway;
    // this keeps the picker from offering a choice that can only fail.
    const parentChoices = customerList.filter((c) =>
      c.isReseller && !c.parentCustomerId && c.customerId !== selected.customerId);

    return (
      <div>
        <button
          type="button"
          style={{ ...styles.btnUnstyled, color: C.goldText, fontSize: 13, marginBottom: 10 }}
          onClick={() => setSelectedId(null)}
        >← All customers</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{selected.name}</h2>
          <span style={statusBadge(selected.status)}>{selected.status}</span>
          {selected.status === 'SUSPENDED' && selected.suspendedBy === 'reseller' && (
            <span style={resellerBadge}>Suspended by its reseller</span>
          )}
          {selected.isReseller && <span style={resellerBadge}>Reseller</span>}
        </div>
        <div style={{ color: C.textMuted, fontSize: 12, marginBottom: selected.parentCustomerId ? 4 : 16 }}>
          <code>{selected.customerId}</code> — the tenant key every one of this company&apos;s rows carries. It
          cannot be changed.
        </div>
        {selected.parentCustomerId && (
          <div style={{ color: C.textSecondary, fontSize: 13, marginBottom: 16 }} data-testid="parent-line">
            Subaccount of{' '}
            <button
              type="button"
              style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600 }}
              onClick={() => select(selected.parentCustomerId!)}
            >{parent?.name ?? selected.parentCustomerId}</button>
          </div>
        )}

        <div style={styles.tabs} role="tablist">
          {detailTabs.map(([id, label]) => (
            <div
              key={id}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === id}
              style={{ ...styles.tab, ...(activeTab === id ? styles.tabActive : {}) }}
              onClick={() => setTab(id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(id); } }}
            >{label}</div>
          ))}
        </div>

        {cleanup && <CleanupReport report={cleanup} onDismiss={() => setCleanup(null)} />}

        {activeTab === 'overview' && (
          <>
            <div style={styles.card}>
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>Company</div>
              <label style={{ fontSize: 12, color: C.textMuted, display: 'block', maxWidth: 420 }}>
                Display name
                <input
                  style={{ ...styles.input, marginTop: 4 }}
                  value={nameDraft ?? selected.name}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
                  disabled={savingName || nameDraft === null || nameDraft.trim() === selected.name}
                  onClick={saveName}
                >{savingName ? 'Saving…' : 'Save name'}</button>
                {nameDraft !== null && (
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                    disabled={savingName}
                    onClick={() => setNameDraft(null)}
                  >Cancel</button>
                )}
              </div>

              <div style={{ marginTop: 20, fontSize: 13, color: C.textSecondary }}>
                <div>Created {new Date(selected.createdAt).toLocaleString()}</div>
                <div>Last updated {new Date(selected.updatedAt).toLocaleString()}</div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Requester contacts</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
                Who at this company may ask for a new portal login. Nothing in the code enforces this — it is
                here so a request for access can be checked against something before it is granted.
              </div>
              {(() => {
                const rows = contactsDraft ?? selected.requesterContacts;
                const edit = (i: number, field: keyof RequesterContact, value: string) =>
                  setContactsDraft(rows.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
                return (
                  <>
                    {rows.length === 0 ? (
                      <div style={{ fontSize: 13, color: C.textMuted }}>None recorded.</div>
                    ) : (
                      <table style={styles.table}>
                        <thead><tr>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Email</th>
                          <th style={styles.th}>Phone</th>
                          <th style={styles.th}></th>
                        </tr></thead>
                        <tbody>
                          {rows.map((rc, i) => (
                            <tr key={i}>
                              <td style={styles.td}>
                                <input
                                  style={styles.input}
                                  aria-label={`Contact ${i + 1} name`}
                                  value={rc.name ?? ''}
                                  onChange={(e) => edit(i, 'name', e.target.value)}
                                />
                              </td>
                              <td style={styles.td}>
                                <input
                                  style={styles.input}
                                  type="email"
                                  aria-label={`Contact ${i + 1} email`}
                                  value={rc.email ?? ''}
                                  onChange={(e) => edit(i, 'email', e.target.value)}
                                />
                              </td>
                              <td style={styles.td}>
                                <input
                                  style={styles.input}
                                  aria-label={`Contact ${i + 1} phone`}
                                  value={rc.phone ?? ''}
                                  onChange={(e) => edit(i, 'phone', e.target.value)}
                                />
                              </td>
                              <td style={styles.td}>
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={savingContacts}
                                  onClick={() => setContactsDraft(rows.filter((_, j) => j !== i))}
                                >Remove</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                      <button
                        style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                        disabled={savingContacts}
                        onClick={() => setContactsDraft([...rows, { name: '', email: '', phone: '' }])}
                      >+ Add contact</button>
                      {contactsDraft !== null && (
                        <>
                          <button
                            style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
                            disabled={savingContacts}
                            onClick={saveContacts}
                          >{savingContacts ? 'Saving…' : 'Save contacts'}</button>
                          <button
                            style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                            disabled={savingContacts}
                            onClick={() => setContactsDraft(null)}
                          >Cancel</button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Suspend is the destructive action on this page. There is no
                delete: a company with history is suspended, never removed. */}
            <div style={{ ...styles.card, borderLeft: `3px solid ${suspended ? C.warn : C.danger}` }}>
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Account status</div>
              {suspended && selected.suspendedBy === 'reseller' ? (
                /* #880 — the reseller suspended this subaccount itself, so the
                   reseller can lift it again. "Hold as 382" is the staff
                   suspend: on an account its reseller suspended it writes 382's
                   own suspension, which the reseller cannot lift. Reactivate is
                   deliberately not offered here — the suspension is the
                   reseller's decision to undo, and once held it is 382's. */
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    This company is <strong>suspended by its reseller</strong>, which can reactivate it from the
                    customer portal. To keep it suspended whatever the reseller does, hold the suspension as
                    382&apos;s — only 382 can then lift it.
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline, color: C.danger, borderColor: C.danger }}
                    disabled={statusBusy}
                    onClick={() => changeStatus('SUSPENDED')}
                  >{statusBusy ? 'Working…' : 'Hold as 382'}</button>
                </>
              ) : suspended ? (
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    This company is <strong>suspended</strong>. Every request from its API clients and portal
                    logins is refused. Reactivating restores the account but does <strong>not</strong> re-enable
                    any credential — enable those individually, so reactivation cannot silently restore access
                    someone revoked on purpose.
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline }}
                    disabled={statusBusy}
                    onClick={() => changeStatus('ACTIVE')}
                  >{statusBusy ? 'Working…' : 'Reactivate company'}</button>
                </>
              ) : confirmStatus ? (
                <>
                  <div style={{ fontSize: 13, color: C.danger, marginBottom: 12 }} role="alert">
                    <strong>Suspend {selected.name}?</strong> Every request from this company is refused from the
                    moment this saves — its integration stops working and its people can no longer sign in to the
                    portal. Its {myClients.length} API client{myClients.length === 1 ? '' : 's'} and its portal
                    logins are then disabled in Keycloak as follow-up work, and you will see exactly which ones
                    succeeded. Nothing is deleted.
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      style={{ ...styles.btn, ...styles.btnDanger }}
                      disabled={statusBusy}
                      onClick={() => changeStatus('SUSPENDED')}
                    >{statusBusy ? 'Suspending…' : 'Confirm suspend'}</button>
                    <button
                      style={{ ...styles.btn, ...styles.btnOutline }}
                      disabled={statusBusy}
                      onClick={() => setConfirmStatus(false)}
                    >Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    Active. Suspending refuses every request from this company immediately and disables its
                    credentials in Keycloak. There is no delete — a company with history is suspended, not
                    removed.
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline, color: C.danger, borderColor: C.danger }}
                    disabled={statusBusy}
                    onClick={() => setConfirmStatus(true)}
                  >Suspend company</button>
                </>
              )}
            </div>

            {/* #867 — the reseller hierarchy, one level deep. A company is a
                reseller, a subaccount of one, or neither; never both. */}
            <div style={styles.card} data-testid="reseller-card">
              <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Reseller</div>
              {selected.parentCustomerId ? (
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    This company is a <strong>subaccount</strong> of {parent?.name ?? selected.parentCustomerId}
                    {' '}(<code>{selected.parentCustomerId}</code>). A subaccount cannot be a reseller itself:
                    subaccounts are one level deep. Detaching it makes it an ordinary customer again; nothing it
                    has submitted moves.
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline }}
                    disabled={hierarchyBusy}
                    onClick={() => changeParent(null)}
                  >{hierarchyBusy ? 'Working…' : 'Detach from reseller'}</button>
                </>
              ) : selected.isReseller ? (
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    This company is a <strong>reseller</strong>: it manages {myChildren.length} subaccount
                    {myChildren.length === 1 ? '' : 's'}, listed on the Subaccounts tab.
                    {myChildren.length > 0 && ' Detach every subaccount before removing the reseller flag.'}
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline }}
                    disabled={hierarchyBusy || myChildren.length > 0}
                    onClick={() => changeReseller(false)}
                  >{hierarchyBusy ? 'Working…' : 'Remove reseller flag'}</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 12 }}>
                    A reseller resells 382&apos;s service and manages its own end customers as subaccounts. Flag
                    this company as one, or attach it to an existing reseller as a subaccount.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <button
                      style={{ ...styles.btn, ...styles.btnOutline }}
                      disabled={hierarchyBusy}
                      onClick={() => changeReseller(true)}
                    >Flag as reseller</button>
                    <select
                      style={{ ...styles.input, width: 'auto', minWidth: 220 }}
                      aria-label="Reseller to attach to"
                      value={parentDraft}
                      disabled={hierarchyBusy || parentChoices.length === 0}
                      onChange={(e) => setParentDraft(e.target.value)}
                    >
                      <option value="">
                        {parentChoices.length === 0 ? 'No resellers to attach to' : 'Attach to reseller…'}
                      </option>
                      {parentChoices.map((c) => (
                        <option key={c.customerId} value={c.customerId}>{c.name} ({c.customerId})</option>
                      ))}
                    </select>
                    <button
                      style={{ ...styles.btn, ...styles.btnOutline }}
                      disabled={hierarchyBusy || parentDraft === ''}
                      onClick={() => changeParent(parentDraft)}
                    >Attach as subaccount</button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'subaccounts' && (
          <>
            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>
              The end customers this reseller manages. Each is an ordinary customer with its own records; attach
              or detach one from that customer&apos;s own Overview.
            </div>
            <div style={styles.card}>
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Customer ID</th>
                  <th style={styles.th}>Status</th>
                </tr></thead>
                <tbody>
                  {myChildren.map((c) => (
                    <tr key={c.customerId}>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600 }}
                          onClick={() => select(c.customerId)}
                        >{c.name}</button>
                      </td>
                      <td style={styles.td}><code style={{ fontSize: 13 }}>{c.customerId}</code></td>
                      <td style={styles.td}><span style={statusBadge(c.status)}>{c.status}</span></td>
                    </tr>
                  ))}
                  {myChildren.length === 0 && (
                    <tr><td style={styles.td} colSpan={3}>
                      <span style={{ color: C.textMuted }}>No subaccounts yet.</span>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'clients' && (
          <>
            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>
              The confidential Keycloak client this company&apos;s integration authenticates with
              (<code>bcid-intake-&lt;slug&gt;</code>), against the Onboarding Intake API — see the{' '}
              <a href={INTAKE_DOCS_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.goldText }}>intake API reference</a>.
              Optional: a company that only uses the portal needs none.
            </div>

            {notConfigured ? (
              <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn }} role="alert">
                <strong>Customer API management is not configured.</strong> The Keycloak admin service account
                (<code>bcid-admin-svc</code>) has not been provisioned with the <code>manage-clients</code> grant
                on this deployment yet. Until then, provision intake clients with the kcadm break-glass procedure
                in the intake-client provisioning runbook.
              </div>
            ) : (
              <>
                {!suspended && (
                  <button
                    style={{ ...styles.btn, ...styles.btnGold, marginBottom: 16 }}
                    onClick={openCreateClient}
                  >+ Issue API Credentials</button>
                )}
                <div style={styles.card}>
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Client ID</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {myClients.map((c) => {
                        const busy = busyClientId === c.clientId;
                        const confirming = confirmClientId === c.clientId;
                        return (
                          <tr key={c.clientId}>
                            <td style={styles.td}><code style={{ fontSize: 13 }}>{c.clientId}</code></td>
                            <td style={styles.td}>
                              <span style={{ ...styles.badge, background: c.enabled ? C.successBg : C.dangerBg, color: c.enabled ? C.success : C.danger }}>
                                {c.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                <button
                                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                  disabled={busy}
                                  onClick={() => rotate(c)}
                                >Rotate secret</button>
                                {confirming ? (
                                  <>
                                    <button
                                      style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }}
                                      disabled={busy}
                                      onClick={() => toggleClient(c)}
                                    >{c.enabled ? 'Confirm disable' : 'Confirm enable'}</button>
                                    <button
                                      style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                      disabled={busy}
                                      onClick={() => setConfirmClientId(null)}
                                    >Cancel</button>
                                  </>
                                ) : (
                                  <button
                                    style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                    disabled={busy}
                                    onClick={() => setConfirmClientId(c.clientId)}
                                  >{c.enabled ? 'Disable' : 'Enable'}</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {myClients.length === 0 && (
                        <tr><td style={styles.td} colSpan={3}>
                          <span style={{ color: C.textMuted }}>
                            No API client for this company. That is a valid state — a portal-only customer needs
                            none.
                          </span>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'logins' && (
          notConfigured ? (
            <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn }} role="alert">
              <strong>Customer API management is not configured.</strong> The Keycloak admin service account
              (<code>bcid-admin-svc</code>) has not been provisioned on this deployment yet.
            </div>
          ) : (
            <CustomerLogins
              customerId={selected.customerId}
              suspended={suspended}
              onChange={loadCredentials}
            />
          )
        )}

        {activeTab === 'registrations' && (
          <>
            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 8 }}>
              Everything this company has submitted, and the vetting case each promoted submission became. The
              company&apos;s own view of the same data is the Registrations tab of the <code>/apply/</code> portal.
            </div>
            {/*
              #539 — the caveat is rendered, not commented. The two RCD columns are
              read straight off the submission payload, so they are what the customer
              ASKED FOR, not necessarily what is live: staff can amend the display
              name or call reason during vetting, and that amendment lands on the case,
              never back on the submission row. An operator reading this table as the
              company's current branding would be reading a stale value with no hint
              that it was stale, so the table says so above itself and points at the
              authority.
            */}
            <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 16 }}>
              Caller display name and Call reason are shown <strong>as submitted</strong>. Staff can amend the
              RCD during vetting; where that happened the case is the authority, not this row.
            </div>
            <div style={styles.card}>
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Submission</th>
                  <th style={styles.th}>Registrant</th>
                  <th style={styles.th}>Caller display name</th>
                  <th style={styles.th}>Call reason</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Case</th>
                  <th style={styles.th}>Submitted</th>
                  <th style={styles.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {mySubmissions.map((s) => (
                    <tr key={s.id}>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}
                          onClick={() => openLinkedSubmission(s.id)}
                        >{s.id}</button>
                      </td>
                      <td style={styles.td}>{s.payload?.legalName ?? '—'}</td>
                      <td style={styles.td}>{s.payload?.displayName || <span style={{ color: C.textMuted }}>—</span>}</td>
                      <td style={styles.td}>{s.payload?.callReason || <span style={{ color: C.textMuted }}>—</span>}</td>
                      <td style={styles.td}>{s.status}</td>
                      <td style={styles.td}>
                        {/*
                          #715 — the marker comes off the row the server just
                          sent, so it is still there after a reload and still
                          there for a registration somebody else deleted last
                          week. The id itself is kept, deleted or not: it is the
                          company's filing history and the only handle on what
                          this submission became.
                        */}
                        {s.promotedCaseId && s.promotedCaseDeletedAt
                          ? <span style={{ color: C.textMuted }}>{s.promotedCaseId} (deleted)</span>
                          : s.promotedCaseId
                            ? <button
                                type="button"
                                style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}
                                disabled={openingCaseId !== null}
                                onClick={() => openPromotedCase(s.promotedCaseId!)}
                              >{s.promotedCaseId}</button>
                            : <span style={{ color: C.textMuted }}>—</span>}
                      </td>
                      <td style={styles.td}>{new Date(s.submittedAt).toLocaleDateString()}</td>
                      {/*
                        #691 — offered only where there is a registration to
                        delete: a submission still in the intake queue has no
                        case behind it, and one already deleted has nothing left
                        to remove.

                        The label is "Delete registration" and not "Delete"
                        because the row is a SUBMISSION and the thing being
                        deleted is the CASE it was promoted into. A bare "Delete"
                        on a row whose first cell is a submission id reads as
                        "withdraw this filing", which is not what happens and is
                        not undone by the dialog agreeing with it afterwards.

                        NO `canAdmin` CHECK HERE, deliberately. Deleting a
                        registration is admin-only and the DELETE route enforces
                        it with a 403, but this whole PAGE already returns
                        "Administrator access required" to a non-admin a few
                        hundred lines up — so a second check on this cell could
                        never be false, and a guard that cannot be false is the
                        same defect as no guard: it reads as protection, it is
                        never exercised, and the test written for it passes on
                        the strength of the gate above rather than of itself.
                        One gate, at the page boundary, where it can be seen.
                      */}
                      <td style={styles.td}>
                        {s.promotedCaseId && !s.promotedCaseDeletedAt && (
                          <button
                            type="button"
                            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }}
                            disabled={deletingCaseId !== null}
                            onClick={() => { void confirmDeleteRegistration(s.promotedCaseId!); }}
                          >Delete registration</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {mySubmissions.length === 0 && (
                    <tr><td style={styles.td} colSpan={8}>
                      <span style={{ color: C.textMuted }}>Nothing submitted by this company yet.</span>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── List ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Customers</h2>
        <button
          style={{ ...styles.btn, ...styles.btnGold }}
          onClick={() => setShowCreate((v) => !v)}
        >{showCreate ? 'Cancel' : '+ Add Customer'}</button>
      </div>

      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
        The companies whose applications this portal accepts. A customer record is the prerequisite for every
        credential: creating a portal login or an API client for a company that does not exist here is refused.
        A company with portal logins and no API client is a normal, supported setup.
      </div>

      {showCreate && (
        <form style={{ ...styles.card, marginBottom: 16 }} onSubmit={submitCreate}>
          <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>Add a customer</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
            <label style={{ fontSize: 12, color: C.textMuted }}>
              Customer ID
              <input
                style={{ ...styles.input, marginTop: 4 }}
                required
                value={form.customerId}
                onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
                placeholder="cust-acme-0001"
              />
            </label>
            <label style={{ fontSize: 12, color: C.textMuted }}>
              Company name
              <input
                style={{ ...styles.input, marginTop: 4 }}
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Acme Corporation"
              />
            </label>
            <button
              style={{ ...styles.btn, ...styles.btnGold }}
              type="submit"
              disabled={creating || form.customerId.trim().length === 0 || form.name.trim().length === 0}
            >{creating ? 'Creating…' : 'Create'}</button>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 10 }}>
            The customer ID is the tenant key every one of this company&apos;s rows will carry, and it is
            <strong> permanent</strong> — there is no rename, because renaming it would orphan every row that
            references it. Lowercase letters, numbers, and hyphens; 2&ndash;31 characters; must start with a
            letter or number. It doubles as this company&apos;s API client id
            (<code>bcid-intake-&lt;customer id&gt;</code>), which is why the shape is restricted.
          </div>
        </form>
      )}

      {loadError && (
        <div style={{ ...styles.card, color: C.danger }} role="alert">Failed to load customers: {loadError}</div>
      )}

      {!loadError && customers && (
        <div style={styles.card}>
          <SearchFilterBar {...bar} />
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Company</th>
              <th style={styles.th}>Customer ID</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>API clients</th>
              <th style={styles.th}>Portal logins</th>
              <th style={styles.th}>Submissions</th>
            </tr></thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.customerId}>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600 }}
                      onClick={() => select(c.customerId)}
                    >{c.name}</button>
                    {c.isReseller && <span style={{ ...resellerBadge, marginLeft: 8 }}>Reseller</span>}
                    {c.parentCustomerId && (
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                        Subaccount of {byId.get(c.parentCustomerId)?.name ?? c.parentCustomerId}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}><code style={{ fontSize: 13 }}>{c.customerId}</code></td>
                  <td style={styles.td}><span style={statusBadge(c.status)}>{c.status}</span></td>
                  {/* An unconfigured Keycloak means "unknown", not "zero" —
                      showing 0 would read as a fact we do not have. */}
                  <td style={styles.td}>{notConfigured ? '—' : (clientsByCustomer.get(c.customerId)?.length ?? 0)}</td>
                  <td style={styles.td}>{notConfigured ? '—' : (loginsByCustomer.get(c.customerId)?.length ?? 0)}</td>
                  <td style={styles.td}>{submissionsByCustomer.get(c.customerId)?.length ?? 0}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td style={styles.td} colSpan={6}>
                  <span style={{ color: C.textMuted }}>
                    {customerList.length === 0
                      ? 'No customers yet. Add one before provisioning any credential.'
                      : 'No companies match the current filters.'}
                  </span>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
